import { useContext, useState } from 'react';

import { Formik, FormikHelpers } from 'formik';
import { Alert, Stack } from 'react-bootstrap';
import * as yup from 'yup';

import InfoIconButton from '@components/Button/InfoIconButton';
import ExpandableSection from '@components/ExpandableSection/ExpandableSection';
import InfoDialog from '@components/Modals/InfoDialog';
import { EMAIL_REGEX, SUPPORT_EMAIL } from '@constants/constants';
import useToast from '@contexts/useToast';
import { OrganizationRole } from '@customTypes/clinician';
import { ToastType } from '@customTypes/toast';
import { ICreateProviderForm } from '@customTypes/user';
import { setOrganizationRoles } from '@helpers/clinician';
import { addNewProvider } from '@store/actions';
import { Context } from '@store/store';

import AddUserForm from './AddUserForm';

import './AddUsers.scss';

const validationSchema = yup.object().shape({
  provider: yup.object().shape({
    name: yup.object().shape({
      first: yup.string().required('First name is required.'),
      middle: yup.string(),
      last: yup.string().required('Last name is required.'),
      credentials: yup.string().when(['$organization_provider_roles'], (data, schema: yup.StringSchema) => {
        if (data[0].includes(OrganizationRole.CLINICIAN)) {
          return schema.required('Please provide credentials.');
        }
        return schema.notRequired();
      }),
    }),
    npi: yup.string().when(['$organization_provider_roles'], (data, schema: yup.StringSchema) => {
      if (data[0].includes(OrganizationRole.CLINICIAN)) {
        return schema.matches(/^\d{9,10}$/, 'NPI must be 9 or 10 digits.').required('Please provide an NPI.');
      }
      return yup
        .string()
        .matches(/^\d{9,10}$/, 'NPI must be 9 or 10 digits.')
        .notRequired();
    }),
    email: yup
      .string()
      .matches(EMAIL_REGEX, ({ value }: { value: string }) => `${value} is invalid. Please provide a valid email.`)
      .required('Please provide a valid email.'),
    phone: yup.string().matches(/^\+?[0-9]{6,}$/, 'Please provide a valid phone number.'),
    fax: yup.string().matches(/^\+?[0-9]{6,}$/, 'Please provide a valid fax number.'),
  }),
  organization_provider_roles: yup
    .array()
    .of(
      yup
        .mixed<OrganizationRole>()
        .oneOf(Object.values(OrganizationRole), 'Invalid organization provider role selected.'),
    )
    .min(1, 'At least one role must be selected.'),
});

export default function AddUsers() {
  const [state, dispatch] = useContext(Context);
  const { addToast } = useToast();

  const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);
  const [isDuplicatedEmail, setIsDuplicatedEmail] = useState(false);

  const onSubmit = async (data: ICreateProviderForm, helpers: FormikHelpers<ICreateProviderForm>) => {
    try {
      setIsDuplicatedEmail(false);
      const res = await addNewProvider(state.clinicId!, data);
      helpers.resetForm();
      const newUsers = [...state.users, setOrganizationRoles(res)];
      dispatch({ type: 'SET_USERS', payload: newUsers });

      if (res.license_status === 'UNVERIFIED') {
        addToast({
          headerTitle: (
            <>
              <span className='fw-bold'>Account successfully created. NPI pending validation.</span>
              <span className='d-block'>
                Account is ready for use, but cannot place an order until NPI validation is completed.
              </span>
            </>
          ),
          type: ToastType.DEFAULT,
          variant: 'warning',
          delay: 10000,
        });
      } else {
        addToast({
          headerTitle: (
            <>
              <span className='fw-bold'>Account successfully created.</span>
              <span className='d-block'>Instructions will be sent to the email provided.</span>
            </>
          ),
          type: ToastType.DEFAULT,
          delay: 10000,
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.status === 409) {
        setIsDuplicatedEmail(true);
      }
    }
  };

  return (
    <>
      <ExpandableSection
        id='addUsers'
        expandableContentId='addUsersContent'
        defaultIsCollapsed
        headerTitle={
          <>
            <span className='text-left'>Add Users</span>
            <InfoIconButton
              onClick={() => setShowInfoDialog(true)}
              aria-label='Organization Default Delegates Info Button'
            />
          </>
        }
      >
        <Formik<ICreateProviderForm>
          validationSchema={validationSchema}
          validateOnBlur
          onSubmit={onSubmit}
          initialValues={{
            provider: {
              name: {
                first: '',
                last: '',
              },
              email: '',
            },
            organization_provider_roles: [],
          }}
        >
          <AddUserForm />
        </Formik>
        <Alert variant='danger' show={isDuplicatedEmail} className='mb-0 mt-n2'>
          <p className='d-flex justify-content-start mb-0'>
            An account with the email you provided already exists. Please provide a different email, or contact
            <a href={`mailto:${SUPPORT_EMAIL}`} className='mx-1 text-emphasis'>
              {SUPPORT_EMAIL}
            </a>{' '}
            for assistance.
          </p>
        </Alert>
      </ExpandableSection>

      {showInfoDialog && (
        <InfoDialog onClose={() => setShowInfoDialog(false)} title='Add New User'>
          <Stack gap={4}>
            <p className='mb-0 fw-bold'>Seamless Clinical Management</p>
            <p className='mb-0'>
              Admins can add and manage users directly in the MyOme Portal, assigning roles such as Admin, Organization
              Delegate, or verified Ordering Provider. NPI and credential fields display only when applicable, keeping
              the workflow clean and intuitive.
            </p>
            <p className='mb-0'>Built to streamline onboarding and empower your clinical team from day one.</p>
          </Stack>
        </InfoDialog>
      )}
    </>
  );
}
